import { PageError } from '@ctw/shared/components/errors/page-error';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  type AdministrativeGender,
  ContactPointSystem,
  ContactPointUse,
  type Patient,
} from '@ctw/shared/types/graphql/__generated__/graphql';
import { getApiServiceBaseUrl, getEnvFromCurrentHostname } from '@ctw/shared/utils/environments';
import { compact, pickBy } from 'lodash-es';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { EHR, SDK } from 'vim-os-js-browser/types';

interface VimPatientInjectorContextValue {
  vimPatient: EHR.Patient;
  vimOS?: SDK;
}

const VimPatientInjectorContext = createContext<VimPatientInjectorContextValue | undefined>(
  undefined,
);

export function useVimPatientContext() {
  const context = useContext(VimPatientInjectorContext);
  if (!context) {
    throw new Error('useVimPatientContext must be used within a VimPatientInjectorProvider');
  }
  return context;
}

export function VimPatientInjector({
  children,
  vimPatient,
  VimPatientIdentifierSystemUrl,
  vimOS,
}: PropsWithChildren<{
  vimPatient: EHR.Patient;
  VimPatientIdentifierSystemUrl: string;
  vimOS?: SDK;
}>) {
  const [patientInjectError, setPatientInjectError] = useState<Error | null>(null);

  const { requestContext, ctwFetch } = useCTW();
  const telemetry = useTelemetry();

  const odsUrl = getApiServiceBaseUrl({
    service: 'fhir',
    env: getEnvFromCurrentHostname({ fallbackEnv: 'sandbox' }),
  });

  // Store the latest patient received before zusToken is available
  const pendingPatientRef = useRef<EHR.Patient | undefined>(undefined);
  const initializedPatientIdRef = useRef<string | undefined>(undefined);

  const buildFhirPatientFromVimPatient = useCallback(
    (sourceVimPatient: EHR.Patient): Patient => {
      const vimPatientId = sourceVimPatient.identifiers?.vimPatientId;

      const vimGender = sourceVimPatient.demographics?.gender;
      const fhirGender =
        vimGender && ['male', 'female', 'other', 'unknown'].includes(vimGender)
          ? (vimGender as unknown as AdministrativeGender)
          : undefined;

      return {
        resourceType: 'Patient',
        // Vim does not provide an active status, so we need to assume active for the ZAP to work
        // TODO: find a way to determine status?
        active: true,
        name: [
          pickBy({
            family: sourceVimPatient.demographics?.lastName,
            given: compact([sourceVimPatient.demographics?.firstName]),
          }),
        ].filter((obj) => Object.keys(obj).length > 0),
        gender: fhirGender,
        birthDate: sourceVimPatient.demographics?.dateOfBirth,
        address: [
          pickBy({
            line: compact([sourceVimPatient.address?.address1]),
            city: sourceVimPatient.address?.city,
            state: sourceVimPatient.address?.state,
            postalCode: sourceVimPatient.address?.zipCode,
          }),
        ].filter((obj) => Object.keys(obj).length > 0),
        telecom: compact([
          sourceVimPatient.contact_info?.homePhoneNumber
            ? {
                system: ContactPointSystem.Phone,
                use: ContactPointUse.Home,
                value: sourceVimPatient.contact_info?.homePhoneNumber,
              }
            : undefined,
          sourceVimPatient.contact_info?.mobilePhoneNumber
            ? {
                system: ContactPointSystem.Phone,
                use: ContactPointUse.Mobile,
                value: sourceVimPatient.contact_info?.mobilePhoneNumber,
              }
            : undefined,
          sourceVimPatient.contact_info?.email
            ? {
                system: ContactPointSystem.Email,
                value: sourceVimPatient.contact_info?.email,
              }
            : undefined,
        ]),
        identifier: compact([
          vimPatientId
            ? {
                system: VimPatientIdentifierSystemUrl,
                value: vimPatientId,
              }
            : undefined,
        ]),
      };
    },
    [VimPatientIdentifierSystemUrl],
  );

  const initPatient = useCallback(
    async (patient: EHR.Patient) => {
      const vimPatientId = patient.identifiers?.vimPatientId;
      if (!(patient && vimPatientId)) {
        const msg = 'Failed to inject Vim patient: received malformed patient data';
        telemetry.trackError({
          message: msg,
          error: new Error(msg),
          context: {
            patient,
          },
        });
        setPatientInjectError(new Error(msg));
        return;
      }
      const resp = await ctwFetch(
        `${odsUrl}/Patient?identifier=${VimPatientIdentifierSystemUrl}|${vimPatientId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${requestContext.authToken}`,
          },
          body: JSON.stringify(buildFhirPatientFromVimPatient(patient)),
        },
      );
      if (!resp.ok) {
        const msg = `Failed to create patient for Vim patient ID ${vimPatientId}`;
        const err = new Error(msg);
        telemetry.trackError({
          message: msg,
          error: err,
          context: {
            response: resp.data,
          },
        });
        setPatientInjectError(err);
        return;
      }
    },
    [
      VimPatientIdentifierSystemUrl,
      buildFhirPatientFromVimPatient,
      telemetry,
      odsUrl,
      ctwFetch,
      requestContext.authToken,
    ],
  );

  useEffect(() => {
    // If no token, store patient for later
    if (!requestContext.authToken && vimPatient) {
      pendingPatientRef.current = vimPatient;
      return;
    }
    if (requestContext.authToken) {
      let patientToProcess: EHR.Patient | undefined;
      if (pendingPatientRef.current) {
        patientToProcess = pendingPatientRef.current;
      } else if (vimPatient) {
        patientToProcess = vimPatient;
      }
      const patientIdToProcess = patientToProcess?.identifiers?.vimPatientId;
      if (patientToProcess && initializedPatientIdRef.current !== patientIdToProcess) {
        initPatient(patientToProcess);
        initializedPatientIdRef.current = patientIdToProcess;
        pendingPatientRef.current = undefined;
      }
    }
  }, [vimPatient, initPatient, requestContext.authToken]);

  const contextValue: VimPatientInjectorContextValue = {
    vimPatient,
    vimOS,
  };

  if (patientInjectError) {
    return (
      <PageError
        title="Unable to load patient data"
        message="There was an error loading the patient from Vim into the system. Please try again or contact support."
        supportEmailSubject="Vim Patient Load Error"
        supportEmailBody={`I encountered the following error while trying to load my patient data from Vim in the Zus Application Platform (ZAP):\n\n${patientInjectError.message}\n\nPlease assist.`}
      />
    );
  }

  return (
    <VimPatientInjectorContext.Provider value={contextValue}>
      {children}
    </VimPatientInjectorContext.Provider>
  );
}
